import React from 'react';
import FeatureCards from "../../components/FeatureCards/FeatureCards";
import CTA from "../../components/Constant/cta/CTA";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import WhySection from "../../components/WhySection/WhySection";
import LocationTimeline from "../../components/LocationTimeline/LocationTimeline";
import IntroSection from "../../components/IntroSection/IntroSection";
import FAQList from "../../components/Constant/FAQCard/FAQCard";
import Testimonials from "../../components/Constant/Testimonials/Testimonials";
import DetailsSection from "../../components/DetailsSection/DetailsSection";
import InfoBlocks from "../../components/InfoBlocks/InfoBlocks";
import ServiceCards from "../../components/ServiceCards/ServiceCards ";

export default function RealEstateInvestmentBaliIndonesia() {

    return (
        <main className='main'>
            <IntroSection
                title="Real Estate Investment in Bali, Indonesia"
                subtitle="Invest, Live & Earn in Asia’s Lifestyle Destination"
                description="Bali has become a global hotspot for investors, expats, and digital entrepreneurs seeking both residency opportunities and strong long-term returns. Indonesia’s government has introduced investment-friendly visas, making Bali a unique destination for combining real estate ownership with residency pathways."
                badges={[
                    "Investor-Friendly Property Market",
                    "Residency via Investment",
                    "High Rental Yields"
                ]}
                buttonText="Request a Bali Real Estate Consultation →"
                buttonLink="/quote"
                imageSrc={require('../../assets/images/real-estate/real-estate-investment-bali-indonesia-hero.png')}
                imageAlt="Bali Real Estate Investment"
            />

            <section className='section'>
                <div className="container">
                    <WhySection
                        title="Why Invest in Bali Real Estate?"
                        blocks={[
                            {
                                items: [
                                    { title: "High Rental Yields", text: "Short-term rentals (Airbnb, villa leasing) often generate 8–12% annual returns." },
                                    { title: "Booming Tourism", text: "Bali attracts over 5 million visitors annually." },
                                    { title: "Lifestyle Destination", text: "Tropical climate, wellness hub, and a strong global expat community." },
                                    { title: "Capital Growth", text: "Property values rising in Canggu, Seminyak, Ubud, and other hotspots." },
                                    { title: "Residency Options", text: "Investor KITAS or Second Home Visa linked to real estate investment." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Property Ownership Rules for Foreigners"
                        blocks={[
                            {
                                items: [
                                    { text: "Leasehold (Hak Sewa) – 25–30 year leases with extension options." },
                                    { text: "Right of Use (Hak Pakai) – allows foreign ownership for personal residence." },
                                    { text: "PT PMA Company – enables property purchase under a foreign-owned company, commonly used for villa rentals or commercial property." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Investment Pathways & Residency"
                        blocks={[
                            {
                                title: "Investor KITAS via Real Estate",
                                items: [
                                    { text: "Set up a PT PMA company for property investment." },
                                    { text: "Typical capital investment: USD 100k+." },
                                    { text: "Eligible for 2–5 year residency permits." }
                                ]
                            },
                            {
                                title: "Second Home Visa (5–10 Years)",
                                items: [
                                    { text: "For high-net-worth individuals seeking long-term stay." },
                                    { text: "Requires approx. IDR 2 billion bank deposit or qualifying investment." },
                                    { text: "Ideal for long-term living in Bali." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Best Areas for Real Estate Investment in Bali"
                        blocks={[
                            {
                                items: [
                                    { text: "Canggu – expat hotspot with coworking, cafés, and surf culture." },
                                    { text: "Seminyak – luxury villas, nightlife, strong rental income." },
                                    { text: "Ubud – wellness retreats, nature tourism, cultural vibrancy." },
                                    { text: "Uluwatu & Bukit – cliffside villas with premium ocean views." },
                                    { text: "Sanur & Nusa Dua – family-friendly areas with resort-style living." }
                                ]
                            }
                        ]}
                    />

                    <ServiceCards
                        title="Typical Investment Models"
                        services={[
                            { title: "Villas for Short-Term Rentals", text: "Ideal for Airbnb and vacation platforms." },
                            { title: "Boutique Hotels", text: "High demand from wellness and retreat travelers." },
                            { title: "Commercial Properties", text: "Shops, cafés, coworking spaces, and mixed-use units." },
                            { title: "Long-Term Residences", text: "For expats, digital nomads, and retirees." }
                        ]}
                    />

                    <WhySection
                        title="Risks & Considerations"
                        blocks={[
                            {
                                items: [
                                    { title: "Legal Compliance", text: "Ensure correct use of structures such as Hak Pakai or PT PMA." },
                                    { title: "Leasehold Security", text: "Lease agreements must be properly registered and documented." },
                                    { title: "Zoning Laws", text: "Tourism and residential zones have different permitted uses." },
                                    { title: "Professional Guidance", text: "Always work with licensed notaries and accredited agents." }
                                ]
                            }
                        ]}
                    />

                    <FAQList data={faqData} title='Frequently Asked Questions (FAQ)'/>

                    <CTA
                        title="Ready to invest in Bali real estate?"
                        description="Offshore Advisory assists with legal structuring (PT PMA), property acquisition, and residency planning (Investor KITAS, Second Home Visa)."
                        buttonText="Book a Bali Real Estate Investment Consultation Today →"
                        buttonLink="/quote"
                    />
                </div>
            </section>
        </main>
    );
};

const faqData = [
    {
        question: "Can foreigners own land in Bali?",
        content: [
            { type: "answer", text: "Not freehold. Foreigners must use Hak Pakai, Hak Sewa, or PT PMA structures." }
        ],
    },
    {
        question: "Can I get residency if I buy property?",
        content: [
            { type: "answer", text: "Yes, via Investor KITAS or Second Home Visa." }
        ],
    },
    {
        question: "What’s the minimum investment?",
        content: [
            { type: "answer", text: "Typically USD 100,000+ for PT PMA linked to Investor KITAS." }
        ],
    },
    {
        question: "How profitable are villa rentals in Bali?",
        content: [
            { type: "answer", text: "Yields often range from 8–12% annually in prime areas." }
        ],
    },
    {
        question: "Can I resell a leasehold property?",
        content: [
            { type: "answer", text: "Yes, leasehold rights can usually be transferred." }
        ],
    },
];

